import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import type { AnyObject } from "../../types";
import DataGrid from "../grid/DataGrid";
import SqlQueryItemHeader from "./SqlQueryItemHeader";
import type { SqlQueryLogItem } from "./types";

export type { SqlQueryLogItem };

interface Props {
  item: SqlQueryLogItem;
}

export default function SqlQueryItem({ item }: Props) {
  const [open, setOpen] = useState(false);

  const result = useMemo(() => item.result ?? [], [item.result]);
  const hasResult = !item.resultsOmitted && item.result != null;

  const columns = useMemo(() => {
    const firstItem = result[0];
    if (firstItem === undefined) return [];
    return Object.keys(firstItem).map((name) => ({ field: name, width: 150 }));
  }, [result]);

  const rowToIndexMap = useMemo(() => new Map(result.map((row, index) => [row, index] as const)), [result]);
  const getRowId = useCallback((row: AnyObject) => rowToIndexMap.get(row) ?? 0, [rowToIndexMap]);

  return (
    <Paper elevation={2}>
      <Grid container sx={{ flex: 1, width: "100%" }}>
        <SqlQueryItemHeader item={item} open={open} onToggle={() => setOpen((prev) => !prev)} />
        {open && (
          <Stack spacing={1} useFlexGap sx={{ width: "100%", px: 2 }}>
            <Typography color="secondary" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", width: "100%" }}>
              {item.queryText}
            </Typography>
            {!hasResult ? (
              <Typography variant="caption" color="textSecondary" sx={{ py: 1 }}>
                {item.resultsOmitted
                  ? "Query result rows are omitted for paged reports. SQL, row count and execution time remain available."
                  : "Results not captured. Enable Include query results before running the query."}
              </Typography>
            ) : result.length === 0 ? (
              <Typography variant="caption" color="textSecondary" sx={{ py: 1 }}>
                Query returned no rows{item.recordsCount ? ` (reported ${item.recordsCount})` : ""}.
              </Typography>
            ) : (
              <Box sx={{ height: 300, width: "100%" }}>
                <DataGrid rows={result} columns={columns} getRowId={getRowId} rowHeight={20} columnHeaderHeight={24} />
              </Box>
            )}
          </Stack>
        )}
      </Grid>
    </Paper>
  );
}
